import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { fmtCents, TXN_LABELS, WALLET_LABELS, fmtDateTime } from "./util";
import { WalletAllocationDonut, WalletArchitecture } from "./viz";
import { Wallet, Coins, Users, TrendingUp, ArrowDownToLine, IdCard, Globe, Gift, ArrowUpRight, ArrowDownRight, Layers, Activity, Zap } from "lucide-react";

const RANGES = ["7D", "30D", "90D", "1Y", "ALL"];

function pctColor(v) { return v >= 0 ? "text-[#00E676]" : "text-[#FF2E63]"; }

function MarketPulse() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    const load = () => api.get("/market/pulse").then((r) => on && setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { on = false; clearInterval(t); };
  }, []);
  const live = d?.source === "live";
  return (
    <div className="rounded-xl border border-white/5 bg-[#0A0B10] p-5" data-testid="market-pulse">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-white flex items-center gap-2"><Activity size={18} className="text-[#2DD4BF]" strokeWidth={1.6} /> Market Pulse</h3>
        <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-[#00E676] animate-pulse" : "bg-slate-600"}`} />
          {live ? `Live · ${(d?.venues_online || []).join(" · ")}` : "Connecting…"}
        </span>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div data-testid="pulse-spreads">
          <p className="text-[10px] uppercase font-mono tracking-widest text-[#2DD4BF] mb-2 flex items-center gap-1.5"><Zap size={12} /> Widest Spreads</p>
          <div className="space-y-1.5">
            {(d?.widest_spreads || []).map((r) => (
              <div key={r.pair} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/20 px-3 py-2">
                <div className="min-w-0"><p className="text-sm text-white">{r.pair}</p><p className="text-[10px] font-mono text-slate-500 truncate">{r.buy_venue}→{r.sell_venue}</p></div>
                <span className={`font-mono text-sm shrink-0 ${r.status === "EXECUTABLE" ? "text-[#00E676]" : "text-[#2DD4BF]"}`}>{r.spread_pct?.toFixed(3)}%</span>
              </div>
            ))}
            {!d?.widest_spreads?.length && <p className="text-xs text-slate-600 font-mono">—</p>}
          </div>
        </div>
        <div data-testid="pulse-gainers">
          <p className="text-[10px] uppercase font-mono tracking-widest text-[#00E676] mb-2 flex items-center gap-1.5"><ArrowUpRight size={12} /> Top Gainers 24h</p>
          <div className="space-y-1.5">
            {(d?.gainers || []).map((r) => (
              <div key={r.pair} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/20 px-3 py-2">
                <div className="min-w-0"><p className="text-sm text-white">{r.base}</p><p className="text-[10px] font-mono text-slate-500">${r.sell?.toLocaleString(undefined, { maximumFractionDigits: 4 })}</p></div>
                <span className={`font-mono text-sm shrink-0 ${pctColor(r.change_24h)}`}>{r.change_24h >= 0 ? "+" : ""}{r.change_24h?.toFixed(2)}%</span>
              </div>
            ))}
            {!d?.gainers?.length && <p className="text-xs text-slate-600 font-mono">—</p>}
          </div>
        </div>
        <div data-testid="pulse-losers">
          <p className="text-[10px] uppercase font-mono tracking-widest text-[#FF2E63] mb-2 flex items-center gap-1.5"><ArrowDownRight size={12} /> Top Losers 24h</p>
          <div className="space-y-1.5">
            {(d?.losers || []).map((r) => (
              <div key={r.pair} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/20 px-3 py-2">
                <div className="min-w-0"><p className="text-sm text-white">{r.base}</p><p className="text-[10px] font-mono text-slate-500">${r.sell?.toLocaleString(undefined, { maximumFractionDigits: 4 })}</p></div>
                <span className={`font-mono text-sm shrink-0 ${pctColor(r.change_24h)}`}>{r.change_24h >= 0 ? "+" : ""}{r.change_24h?.toFixed(2)}%</span>
              </div>
            ))}
            {!d?.losers?.length && <p className="text-xs text-slate-600 font-mono">—</p>}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
        <p className="text-[10px] text-slate-600 font-mono">Real prices from public exchange APIs · read-only</p>
        <Link to="/dashboard/q-core" className="text-xs text-[#2DD4BF] hover:text-white transition-colors">Open Live Terminal →</Link>
      </div>
    </div>
  );
}

function WalletCard({ title, icon: Icon, w, accent, extra }) {
  return (
    <div className="group rounded-xl border border-white/5 bg-[#0A0B10] p-5 hover:border-white/10 transition-colors" data-testid={`wallet-${title}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="flex items-center gap-2 text-sm text-slate-400"><Icon size={18} strokeWidth={1.5} className={accent} /> {WALLET_LABELS[title]}</span>
      </div>
      <p className="font-mono text-2xl md:text-3xl text-white">{fmtCents(w?.available_cents)}</p>
      <p className="text-xs text-slate-500 mt-1">Available balance</p>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg border border-white/5 bg-black/20 px-3 py-2">
          <p className="text-slate-500">Pending</p>
          <p className="font-mono text-slate-300 mt-0.5">{fmtCents(w?.pending_cents)}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-black/20 px-3 py-2">
          <p className="text-slate-500">{extra}</p>
          <p className="font-mono text-slate-300 mt-0.5">{fmtCents(extra === "Total withdrawn" ? w?.total_withdrawn_cents : w?.total_credited_cents)}</p>
        </div>
      </div>
    </div>
  );
}

const QUICK = [
  { to: "/dashboard/quantum-verse", label: "Choose Portfolio", icon: Globe, testid: "qa-portfolio" },
  { to: "/dashboard/withdrawals", label: "Make Withdrawal", icon: ArrowDownToLine, testid: "qa-withdraw" },
  { to: "/dashboard/quantum-verse?tab=active", label: "Active Portfolios", icon: TrendingUp, testid: "qa-active" },
  { to: "/dashboard/membership", label: "Membership", icon: IdCard, testid: "qa-membership" },
  { to: "/dashboard/referrals", label: "Referral Bonus", icon: Gift, testid: "qa-referral" },
];

function StatTile({ label, value }) {
  return (
    <div className="rounded-lg border border-white/5 bg-black/20 p-3">
      <p className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{label}</p>
      <p className="font-mono text-lg text-white mt-1">{value}</p>
    </div>
  );
}

export default function Overview() {
  const [summary, setSummary] = useState(null);
  const [perf, setPerf] = useState(null);
  const [range, setRange] = useState("30D");
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/activities?limit=8").then((r) => setTxns(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/performance?range=${range}`).then((r) => setPerf(r.data)).catch(() => {});
  }, [range]);

  const w = summary?.wallets || {};
  const p = summary?.portfolio || {};
  const e = summary?.earnings || {};

  return (
    <div className="space-y-6">
      {/* Wallets + Active portfolios */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <WalletCard title="main" icon={Wallet} w={w.main} accent="text-[#2DD4BF]" extra="Total withdrawn" />
        <WalletCard title="staking" icon={Coins} w={w.staking} accent="text-[#0E9E90]" extra="Total credited" />
        <WalletCard title="referral" icon={Users} w={w.referral} accent="text-[#00E676]" extra="Total credited" />
        <div className="rounded-xl border border-white/5 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-5" data-testid="active-portfolios-card">
          <div className="flex items-center justify-between mb-4">
            <span className="flex items-center gap-2 text-sm text-slate-400"><Layers size={18} strokeWidth={1.5} className="text-[#2DD4BF]" /> Active Portfolios</span>
          </div>
          <p className="font-mono text-2xl md:text-3xl text-white">{p.active_count ?? 0}</p>
          <p className="text-xs text-slate-500 mt-1">Currently accruing</p>
          <div className="mt-4">
            <Link to="/dashboard/quantum-verse" className="inline-flex items-center gap-1.5 text-xs text-[#2DD4BF] hover:text-white transition-colors">
              Manage in Quantum Verse <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="quick-actions">
        {QUICK.map((q) => (
          <Link key={q.testid} to={q.to} data-testid={q.testid} className="group flex flex-col items-center justify-center gap-2 rounded-xl border border-white/5 bg-[#0A0B10] p-4 hover:border-[#0E9E90]/40 hover:bg-[#0E9E90]/[0.04] transition-colors text-center">
            <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#0E9E90]/10 text-[#2DD4BF] group-hover:scale-105 transition-transform"><q.icon size={20} strokeWidth={1.5} /></span>
            <span className="text-xs text-slate-300">{q.label}</span>
          </Link>
        ))}
      </div>

      {/* Performance + allocation */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-white/5 bg-[#0A0B10] p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold text-white">Portfolio Performance</h3>
            <div className="flex gap-1">
              {RANGES.map((rg) => (
                <button key={rg} onClick={() => setRange(rg)} data-testid={`perf-range-${rg}`} className={`px-2.5 py-1 rounded-md text-xs font-mono transition-colors ${range === rg ? "bg-[#0E9E90]/15 text-[#2DD4BF]" : "text-slate-500 hover:text-white"}`}>{rg}</button>
              ))}
            </div>
          </div>
          <div className="h-56">
            {perf?.series?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={perf.series} margin={{ left: -12, right: 6, top: 6 }}>
                  <defs>
                    <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2DD4BF" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2DD4BF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1E293B" vertical={false} />
                  <XAxis dataKey="date" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(c) => `$${(c / 100).toFixed(0)}`} />
                  <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtCents(v)} labelStyle={{ color: "#94A3B8" }} />
                  <Area type="monotone" dataKey="cumulative_cents" name="Cumulative earnings" stroke="#2DD4BF" strokeWidth={2} fill="url(#pv)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-slate-500 font-mono">No earnings yet — purchase a portfolio to begin accruing.</div>
            )}
          </div>
        </div>

        <WalletAllocationDonut wallets={w} />
      </div>

      {/* Market Pulse */}
      <MarketPulse />

      {/* Architecture + Portfolio summary */}
      <div className="grid lg:grid-cols-3 gap-4">
        <WalletArchitecture />
        <div className="lg:col-span-2 rounded-xl border border-white/5 bg-[#0A0B10] p-5">
          <h3 className="font-semibold text-white mb-4">Portfolio Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            <StatTile label="Active" value={p.active_count ?? 0} />
            <StatTile label="Active Value" value={fmtCents(p.active_value_cents)} />
            <StatTile label="Deposits" value={fmtCents(p.total_deposits_cents)} />
            <StatTile label="Top-ups" value={fmtCents(p.total_topups_cents)} />
            <StatTile label="Total Profit" value={fmtCents(p.total_profit_cents)} />
            <StatTile label="Withdrawn" value={fmtCents(p.total_withdrawn_cents)} />
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-2.5">
            <StatTile label="Referral Earnings" value={fmtCents(e.referral_cents)} />
            <StatTile label="Total Earned (all time)" value={fmtCents(e.total_earned_cents)} />
          </div>
        </div>
      </div>

      {/* Latest transactions */}
      <div className="rounded-xl border border-white/5 bg-[#0A0B10] overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="font-semibold text-white">Latest Transactions</h3>
          <Link to="/dashboard/activities" className="text-sm text-[#2DD4BF] hover:text-white transition-colors">View all activities →</Link>
        </div>
        {txns.length ? (
          <div className="divide-y divide-white/5">
            {txns.map((t) => {
              const credit = t.amount_cents >= 0;
              return (
                <div key={t.id} className="flex items-center justify-between px-5 py-3.5" data-testid="latest-txn">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`grid place-items-center h-8 w-8 rounded-lg ${credit ? "bg-[#00E676]/10 text-[#00E676]" : "bg-[#FF2E63]/10 text-[#FF2E63]"}`}>
                      {credit ? <ArrowDownRight size={15} /> : <ArrowUpRight size={15} />}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{TXN_LABELS[t.txn_type] || t.txn_type}</p>
                      <p className="text-xs text-slate-500 font-mono">{t.txn_id} · {fmtDateTime(t.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-mono text-sm ${credit ? "text-[#00E676]" : "text-[#FF2E63]"}`}>{credit ? "+" : ""}{fmtCents(t.amount_cents)}</p>
                    <p className="text-[10px] uppercase font-mono text-slate-500">{WALLET_LABELS[t.wallet] || t.wallet}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="p-6 text-sm text-slate-500 font-mono">No transactions yet.</p>
        )}
      </div>

      <p className="text-center text-xs text-slate-600 font-mono">
        Figures derive from the immutable ledger. Trading involves risk; past performance does not guarantee future results.
      </p>
    </div>
  );
}
